import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProfileByUsername, getUserProducts } from '../lib/burokshop';
import type { UserProfile, UserMarketplaceProducts } from '../lib/burokshop/types';
import Footer from '../components/layout/footer';
import { createClient } from '@supabase/supabase-js';
import { StarIcon, MapPinIcon, PhoneIcon, HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);
// Social media icons - using simple SVG icons
const InstagramIcon = () => (
  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const TwitterIcon = () => (
  <img 
    src="/assets/images/twitter-x-logo-white.svg" 
    alt="X (Twitter)" 
    className="h-6 w-6"
  />
);

const TikTokIcon = () => (
  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const SnapchatIcon = () => (
  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 256 256">
    <path d="M249.36426,186.78713a11.9214,11.9214,0,0,0-6.76856-8.958c-1.3872-.66748-25.52636-12.74414-38.49853-48.14355l16.35986-6.544a12,12,0,0,0-8.91406-22.2832L198.04,106.25979A162.8006,162.8006,0,0,1,196,80,68,68,0,0,0,60,80a162.78977,162.78977,0,0,1-2.04,26.25928l-13.50342-5.40088a11.99983,11.99983,0,0,0-8.91308,22.2832l16.35986,6.54346c-12.93359,35.293-36.96582,47.40381-38.48535,48.13721a12.00069,12.00069,0,0,0-3.10742,19.61035c8.21582,7.71094,19.24609,8.44726,28.97754,9.09765,5.09472.33985,10.86816.72559,13.584,2.22071,2.59131,1.42578,5.94434,6.02246,8.90234,10.07715,5.51661,7.5625,12.38184,16.97363,23.479,19.81347a26.73407,26.73407,0,0,0,6.65771.8291c8.19825,0,15.83789-3.56054,22.74854-6.78125C119.60742,230.38381,124.72266,228,128,228s8.39355,2.38379,13.34082,4.68945c8.74951,4.07715,18.665,8.70508,29.40723,5.95118,11.0957-2.83887,17.96093-12.25,23.47754-19.81153,2.958-4.05566,6.311-8.65234,8.90234-10.07812,2.71582-1.49512,8.49023-1.88086,13.584-2.22071,9.73194-.65039,20.76172-1.38671,28.978-9.09765A11.99019,11.99019,0,0,0,249.36426,186.78713Zm-57.81006.93848c-7.26367,3.998-12.28662,10.88379-16.71826,16.959-3.25147,4.457-7.29785,10.00488-10.03711,10.70508-2.42725.61816-8.72363-2.31152-13.32031-4.4541C144.502,207.68459,136.59473,204,128,204s-16.50195,3.68457-23.478,6.93555c-4.59619,2.14355-10.88623,5.07422-13.31982,4.45508-2.74024-.70118-6.78662-6.249-10.03809-10.70606-4.43164-6.07519-9.4541-12.96-16.71728-16.959-5.75537-3.168-12.252-4.21485-18.43067-4.76172C63.22314,165.9717,84,134.22756,84,80a44,44,0,0,1,88,0c0,54.22754,20.77686,85.97168,37.98438,102.96387C203.80566,183.51076,197.30957,184.55764,191.5542,187.72561Z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const YoutubeIcon = () => (
  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

export default function UserProfilePage() {
  const { username } = useParams<{ username: string }>();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [products, setProducts] = useState<UserMarketplaceProducts | null>(null);
  const [logos, setLogos] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [activeSocialNetworks, setActiveSocialNetworks] = useState<string[]>([]);
  const [isVerified, setIsVerified] = useState(false);
  const [profileUsernames, setProfileUsernames] = useState<{
    instagram_username: string | null;
    twitter_username: string | null;
    tiktok_username: string | null;
    snapchat_username: string | null;
    linkedin_username: string | null;
    youtube_username: string | null;
    facebook_username: string | null;
  } | null>(null);
  const [activeShops, setActiveShops] = useState<Array<'kiosk' | 'boutique' | 'restaurant'>>([]);
  const [activeTab, setActiveTab] = useState<'kiosk' | 'boutique' | 'restaurant' | null>(null);
  const [userKiosks, setUserKiosks] = useState<any[]>([]);
  const [userBoutiques, setUserBoutiques] = useState<any[]>([]);
  const [userRestaurants, setUserRestaurants] = useState<any[]>([]);
  const [userFollowingStates, setUserFollowingStates] = useState<Record<string, boolean>>({});
  const [userFollowingAnimating, setUserFollowingAnimating] = useState<Record<string, boolean>>({});
  const [showBusinessProfile, setShowBusinessProfile] = useState(false);
  const [businessProfile, setBusinessProfile] = useState<any>(null); // Le profil depuis business_profiles
  const [businessKiosks, setBusinessKiosks] = useState<any[]>([]);
  const [businessBoutiques, setBusinessBoutiques] = useState<any[]>([]);
  const [businessRestaurants, setBusinessRestaurants] = useState<any[]>([]);
  const [businessActiveTab, setBusinessActiveTab] = useState<'kiosk' | 'boutique' | 'restaurant'>('kiosk');
  const [followingStates, setFollowingStates] = useState<Record<string, boolean>>({});
  const [followingAnimating, setFollowingAnimating] = useState<Record<string, boolean>>({});
  const [showContactInfoPublic, setShowContactInfoPublic] = useState(false);
  const [businessSocialNetworks, setBusinessSocialNetworks] = useState<string[]>([]);
  const [businessProfileUsernames, setBusinessProfileUsernames] = useState<{
    instagram_username: string | null;
    twitter_username: string | null;
    tiktok_username: string | null;
    snapchat_username: string | null;
    linkedin_username: string | null;
    youtube_username: string | null;
    facebook_username: string | null;
  } | null>(null);

  useEffect(() => {
    async function loadLogos() {
      try {
        const { data } = await supabase
          .from('miniapps')
          .select('name, logo')
          .in('name', ['Kiosk', 'Boutiques', 'Restaurants']);

        if (data) {
          const logoMap: Record<string, string> = {};
          data.forEach((app) => {
            const key = app.name.toLowerCase();
            if (key === 'kiosk') logoMap['kiosk'] = app.logo || '';
            else if (key === 'boutiques') logoMap['boutique'] = app.logo || '';
            else if (key === 'restaurants') logoMap['restaurant'] = app.logo || '';
          });
          setLogos(logoMap);
        }
      } catch (error) {
        console.error('Error loading logos:', error);
      }
    }
    loadLogos();
  }, []);

  useEffect(() => {
    async function loadProfile() {
      if (!username) return;

      setLoading(true);
      try {
        const profileData = await getProfileByUsername(username);
        if (profileData) {
          setProfile(profileData);
          const productsData = await getUserProducts(profileData.id);
          setProducts(productsData);

          // Récupérer les réseaux sociaux activés depuis shoplink_social_networks
          const { data: socialData, error: socialError } = await supabase
            .from('shoplink_social_networks')
            .select('social_network_type')
            .eq('user_id', profileData.id)
            .eq('is_active', true)
            .limit(3);

          if (socialError) {
            console.error('Error fetching social networks:', socialError);
            setActiveSocialNetworks([]);
          } else if (socialData && socialData.length > 0) {
            setActiveSocialNetworks(socialData.map((s) => s.social_network_type));
          } else {
            setActiveSocialNetworks([]);
          }

          // Récupérer les shops activés depuis shoplink_user_shops
          const { data: shopsData } = await supabase
            .from('shoplink_user_shops')
            .select('shop_type')
            .eq('user_id', profileData.id)
            .eq('is_active_in_shoplink', true);

          if (shopsData && shopsData.length > 0) {
            const shops = shopsData.map((s) => s.shop_type as 'kiosk' | 'boutique' | 'restaurant');
            setActiveShops(shops);
          } else {
            setActiveShops([]);
          }
          
          // Charger tous les kiosks, boutiques et restaurants de l'utilisateur
          const [kiosksResult, boutiquesResult, restaurantsResult] = await Promise.all([
            supabase
              .from('kiosk_profiles')
              .select('*')
              .eq('user_id', profileData.id)
              .order('is_active', { ascending: false })
              .order('is_live', { ascending: false })
              .order('created_at', { ascending: false }),
            supabase
              .from('boutique_profiles')
              .select('*')
              .eq('user_id', profileData.id)
              .order('is_active', { ascending: false })
              .order('is_live', { ascending: false })
              .order('created_at', { ascending: false }),
            supabase
              .from('restaurant_profiles')
              .select('*')
              .eq('user_id', profileData.id)
              .order('is_active', { ascending: false })
              .order('is_live', { ascending: false })
              .order('created_at', { ascending: false })
          ]);

          if (kiosksResult.data) {
            setUserKiosks(kiosksResult.data);
          }
          if (boutiquesResult.data) {
            setUserBoutiques(boutiquesResult.data);
          }
          if (restaurantsResult.data) {
            setUserRestaurants(restaurantsResult.data);
          }
          
          // Définir le tab actif par défaut (celui qui a le plus d'éléments)
          if (kiosksResult.data && kiosksResult.data.length > 0) {
          setActiveTab('kiosk');
          } else if (boutiquesResult.data && boutiquesResult.data.length > 0) {
            setActiveTab('boutique');
          } else if (restaurantsResult.data && restaurantsResult.data.length > 0) {
            setActiveTab('restaurant');
          } else {
            setActiveTab('kiosk');
          }

          // Récupérer le statut de vérification ET les usernames directement depuis la DB (pour éviter le cache)
          const { data: profileDataFull } = await supabase
            .from('profiles')
            .select('show_badge_verified, instagram_username, twitter_username, tiktok_username, snapchat_username, linkedin_username, youtube_username, facebook_username')
            .eq('id', profileData.id)
            .single();

          if (profileDataFull) {
            setIsVerified(profileDataFull.show_badge_verified || false);
            setProfileUsernames({
              instagram_username: profileDataFull.instagram_username,
              twitter_username: profileDataFull.twitter_username,
              tiktok_username: profileDataFull.tiktok_username,
              snapchat_username: profileDataFull.snapchat_username,
              linkedin_username: profileDataFull.linkedin_username,
              youtube_username: profileDataFull.youtube_username,
              facebook_username: profileDataFull.facebook_username
            });
          }

          // Vérifier si on doit afficher le profil business
          const { data: businessSettings } = await supabase
            .from('business_settings')
            .select('business_profile_id, show_business_profile_in_payments, show_contact_info_public')
            .eq('user_id', profileData.id)
            .eq('show_business_profile_in_payments', true)
            .single();

          if (businessSettings && businessSettings.business_profile_id) {
            const businessProfileId = businessSettings.business_profile_id;

            // Charger le profil business depuis business_profiles
            const { data: businessProfileData } = await supabase
              .from('business_profiles')
              .select('*')
              .eq('id', businessProfileId)
              .single();

            if (businessProfileData) {
              setShowBusinessProfile(true);
              
              // Charger show_contact_info_public depuis business_settings
              setShowContactInfoPublic(businessSettings?.show_contact_info_public || false);

              // Charger les réseaux sociaux activés depuis shoplink_social_networks pour le business
              // Utiliser le user_id du business profile (businessProfileData.user_id)
              const { data: businessSocialData } = await supabase
                .from('shoplink_social_networks')
                .select('social_network_type')
                .eq('user_id', businessProfileData.user_id)
                .eq('is_active', true);

              if (businessSocialData && businessSocialData.length > 0) {
                setBusinessSocialNetworks(businessSocialData.map((s) => s.social_network_type));
              }

              // Charger les usernames des réseaux sociaux depuis business_profiles
              setBusinessProfileUsernames({
                instagram_username: businessProfileData.instagram_username || null,
                twitter_username: businessProfileData.twitter_username || null,
                tiktok_username: businessProfileData.tiktok_username || null,
                snapchat_username: businessProfileData.snapchat_username || null,
                linkedin_username: businessProfileData.linkedin_username || null,
                youtube_username: businessProfileData.youtube_username || null,
                facebook_username: businessProfileData.facebook_username || null
              });
              
              // Mapper les données du business_profile
              setBusinessProfile({
                id: businessProfileData.id,
                business_name: businessProfileData.business_name,
                business_description: businessProfileData.business_description,
                logo_url: businessProfileData.business_logo_url,
                banner_url: businessProfileData.business_banner_url,
                business_phone: businessProfileData.business_phone,
                business_email: businessProfileData.business_email,
                street: businessProfileData.business_street || '',
                city: businessProfileData.business_city || '',
                rating: 4.8, // Valeur par défaut, peut être calculée plus tard
                review_count: 0,
                total_orders: 0,
                is_verified: businessProfileData.show_badge_verified || false,
                badge_verified: businessProfileData.show_badge_verified || false,
                is_open: true
              });

              // Charger tous les kiosks, boutiques et restaurants associés
              const [kiosksResult, boutiquesResult, restaurantsResult] = await Promise.all([
                supabase
                  .from('kiosk_profiles')
                  .select('*')
                  .eq('business_profile_id', businessProfileId)
                  .order('is_active', { ascending: false })
                  .order('is_live', { ascending: false })
                  .order('created_at', { ascending: false }),
                supabase
                  .from('boutique_profiles')
                  .select('*')
                  .eq('business_profile_id', businessProfileId)
                  .order('is_active', { ascending: false })
                  .order('is_live', { ascending: false })
                  .order('created_at', { ascending: false }),
                supabase
                  .from('restaurant_profiles')
                  .select('*')
                  .eq('business_profile_id', businessProfileId)
                  .order('is_active', { ascending: false })
                  .order('is_live', { ascending: false })
                  .order('created_at', { ascending: false })
              ]);

              if (kiosksResult.data) {
                setBusinessKiosks(kiosksResult.data);
              }
              if (boutiquesResult.data) {
                setBusinessBoutiques(boutiquesResult.data);
              }
              if (restaurantsResult.data) {
                setBusinessRestaurants(restaurantsResult.data);
              }

              // Définir le tab actif par défaut (celui qui a le plus d'éléments)
              if (kiosksResult.data && kiosksResult.data.length > 0) {
                setBusinessActiveTab('kiosk');
              } else if (boutiquesResult.data && boutiquesResult.data.length > 0) {
                setBusinessActiveTab('boutique');
              } else if (restaurantsResult.data && restaurantsResult.data.length > 0) {
                setBusinessActiveTab('restaurant');
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoading(false);
      }
    }
    loadProfile();
  }, [username]);

  // Réseaux sociaux Burok par défaut
  const burokSocialLinks = [
    {
      platform: 'Twitter',
      username: 'burokmobile',
      url: 'https://twitter.com/burokmobile'
    },
    {
      platform: 'Instagram',
      username: 'burokmobile',
      url: 'https://instagram.com/burokmobile'
    },
    {
      platform: 'TikTok',
      username: 'burokmobile',
      url: 'https://tiktok.com/@burokmobile'
    }
  ];

  // Construire les liens sociaux selon les réseaux activés avec useMemo pour recalculer quand les données changent
  // IMPORTANT: Ce hook doit être appelé AVANT les returns conditionnels
  const socialLinks = useMemo(() => {
    // Si aucun réseau activé, afficher les réseaux Burok
    if (activeSocialNetworks.length === 0) {
      return burokSocialLinks;
    }

    // Si profile n'est pas encore chargé, retourner les liens Burok par défaut
    if (!profile) {
      return burokSocialLinks;
    }

    // Attendre que profileUsernames soit chargé, sinon utiliser ceux du profile (fallback)
    // Mais préférer toujours profileUsernames car il vient directement de la DB sans cache
    const usernames = profileUsernames || {
      instagram_username: profile.instagram_username || null,
      twitter_username: profile.twitter_username || null,
      tiktok_username: profile.tiktok_username || null,
      snapchat_username: profile.snapchat_username || null,
      linkedin_username: profile.linkedin_username || null,
      youtube_username: profile.youtube_username || null,
      facebook_username: profile.facebook_username || null
    };

    // Helper function pour vérifier si un username est valide (non null, non undefined, non vide)
    const getUsername = (username: string | null | undefined): string => {
      if (!username) {
        return 'burokmobile';
      }
      const trimmed = username.trim();
      return trimmed !== '' ? trimmed : 'burokmobile';
    };

    const socialLinksMap: Record<string, { platform: string; username: string; url: string }> = {
      instagram: {
        platform: 'Instagram',
        username: getUsername(usernames.instagram_username),
        url: `https://instagram.com/${getUsername(usernames.instagram_username)}`
      },
      twitter: {
        platform: 'Twitter',
        username: getUsername(usernames.twitter_username),
        url: `https://twitter.com/${getUsername(usernames.twitter_username)}`
      },
      tiktok: {
        platform: 'TikTok',
        username: getUsername(usernames.tiktok_username),
        url: `https://tiktok.com/@${getUsername(usernames.tiktok_username)}`
      },
      snapchat: {
        platform: 'Snapchat',
        username: getUsername(usernames.snapchat_username),
        url: `https://snapchat.com/add/${getUsername(usernames.snapchat_username)}`
      },
      linkedin: {
        platform: 'LinkedIn',
        username: getUsername(usernames.linkedin_username),
        url: `https://linkedin.com/in/${getUsername(usernames.linkedin_username)}`
      },
      youtube: {
        platform: 'YouTube',
        username: getUsername(usernames.youtube_username),
        url: `https://youtube.com/@${getUsername(usernames.youtube_username)}`
      },
      facebook: {
        platform: 'Facebook',
        username: getUsername(usernames.facebook_username),
        url: `https://facebook.com/${getUsername(usernames.facebook_username)}`
      }
    };

    return activeSocialNetworks
      .map((networkType) => socialLinksMap[networkType])
      .filter(Boolean) as Array<{ platform: string; username: string; url: string }>;
  }, [activeSocialNetworks, profileUsernames, profile]);

  // useMemo pour les réseaux sociaux du business
  const businessSocialLinks = useMemo(() => {
    if (!businessProfileUsernames) return [];

    const getUsername = (username: string | null) => username || '';

    const socialLinksMap: Record<string, { platform: string; username: string; url: string }> = {
      instagram: {
        platform: 'Instagram',
        username: getUsername(businessProfileUsernames.instagram_username),
        url: `https://instagram.com/${getUsername(businessProfileUsernames.instagram_username)}`
      },
      twitter: {
        platform: 'Twitter',
        username: getUsername(businessProfileUsernames.twitter_username),
        url: `https://twitter.com/${getUsername(businessProfileUsernames.twitter_username)}`
      },
      tiktok: {
        platform: 'TikTok',
        username: getUsername(businessProfileUsernames.tiktok_username),
        url: `https://tiktok.com/@${getUsername(businessProfileUsernames.tiktok_username)}`
      },
      snapchat: {
        platform: 'Snapchat',
        username: getUsername(businessProfileUsernames.snapchat_username),
        url: `https://snapchat.com/add/${getUsername(businessProfileUsernames.snapchat_username)}`
      },
      linkedin: {
        platform: 'LinkedIn',
        username: getUsername(businessProfileUsernames.linkedin_username),
        url: `https://linkedin.com/in/${getUsername(businessProfileUsernames.linkedin_username)}`
      },
      youtube: {
        platform: 'YouTube',
        username: getUsername(businessProfileUsernames.youtube_username),
        url: `https://youtube.com/@${getUsername(businessProfileUsernames.youtube_username)}`
      },
      facebook: {
        platform: 'Facebook',
        username: getUsername(businessProfileUsernames.facebook_username),
        url: `https://facebook.com/${getUsername(businessProfileUsernames.facebook_username)}`
      }
    };

    // Si businessSocialNetworks a des entrées, utiliser seulement ceux qui sont activés
    // Sinon, afficher tous les réseaux qui ont un username
    if (businessSocialNetworks.length > 0) {
      return businessSocialNetworks
        .map((networkType) => socialLinksMap[networkType])
        .filter((link) => link && link.username) as Array<{ platform: string; username: string; url: string }>;
    } else {
      // Afficher tous les réseaux qui ont un username, même si pas dans shoplink_social_networks
      return Object.values(socialLinksMap).filter((link) => link.username) as Array<{ platform: string; username: string; url: string }>;
    }
  }, [businessSocialNetworks, businessProfileUsernames]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-lg text-neutral-500 dark:text-neutral-400">Un instant...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold text-neutral-900 dark:text-white">Profil non trouvé</h1>
          <p className="text-neutral-500 dark:text-neutral-400">
            L'utilisateur &quot;{username}&quot; n'existe pas ou son profil n'est pas public.
          </p>
        </div>
      </div>
    );
  }

  // Si showBusinessProfile est true, afficher le profil business avec le design de KioskProfilePage
  // Fonction pour gérer le suivi (business profile)
  const handleFollow = (id: string) => {
    setFollowingAnimating(prev => ({ ...prev, [id]: true }));
    setTimeout(() => {
      setFollowingStates(prev => ({ ...prev, [id]: !prev[id] }));
      setFollowingAnimating(prev => ({ ...prev, [id]: false }));
    }, 300);
  };

  // Fonction pour gérer le suivi (user profile)
  const handleUserFollow = (id: string) => {
    setUserFollowingAnimating(prev => ({ ...prev, [id]: true }));
    setTimeout(() => {
      setUserFollowingStates(prev => ({ ...prev, [id]: !prev[id] }));
      setUserFollowingAnimating(prev => ({ ...prev, [id]: false }));
    }, 300);
  };

  // Si showBusinessProfile est true, afficher le profil business avec tabs
  if (showBusinessProfile && businessProfile) {
    const shopNames: Record<'kiosk' | 'boutique' | 'restaurant', string> = {
      kiosk: 'Kiosk',
      boutique: 'Boutiques',
      restaurant: 'Restaurants'
    };
    const shopCounts: Record<'kiosk' | 'boutique' | 'restaurant', number> = {
      kiosk: businessKiosks.length,
      boutique: businessBoutiques.length,
      restaurant: businessRestaurants.length
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900">
        {/* Banner */}
        {businessProfile.banner_url && (
          <div className="relative h-48 w-full overflow-hidden sm:h-64 md:h-80">
            <img
              src={businessProfile.banner_url}
              alt="Banner"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-white/20 backdrop-blur-sm blur-xl" />
                <img
                  src="/assets/images/Shoplinkpng.png"
                  alt="ShopLink Logo"
                  className="relative h-16 w-auto object-contain drop-shadow-2xl sm:h-20 md:h-24"
                />
              </div>
            </div>
          </div>
        )}

        {/* Bannière défilante avec logos Burok */}
        <div className="relative w-full overflow-hidden bg-gradient-to-r from-[#ff0442] via-[#ff1a4d] to-[#ff0442] py-3">
          <div className="flex animate-scroll-left items-center gap-8 whitespace-nowrap">
            {[
              '/assets/BurokLogoModern/BurokModernLogoWhite.png',
              '/assets/BurokLogoModern/BurokModernLogoBlack.png',
              '/assets/BurokLogoModern/BurokModernLogoOrange.png',
              '/assets/BurokLogoModern/BurokModernLogoPurple.png',
              '/assets/BurokLogoModern/BurokModernLogoBlue.png',
              '/assets/BurokLogoModern/BurokModernLogoYellow.png',
              '/assets/BurokLogoModern/BurokModernLogoGreen.png',
              '/assets/BurokLogoModern/BurokModernLogoRed.png',
              '/assets/BurokLogoModern/BurokModernLogoWhite.png',
              '/assets/BurokLogoModern/BurokModernLogoBlack.png',
              '/assets/BurokLogoModern/BurokModernLogoOrange.png',
              '/assets/BurokLogoModern/BurokModernLogoPurple.png',
              '/assets/BurokLogoModern/BurokModernLogoBlue.png',
              '/assets/BurokLogoModern/BurokModernLogoYellow.png',
              '/assets/BurokLogoModern/BurokModernLogoGreen.png',
              '/assets/BurokLogoModern/BurokModernLogoRed.png',
            ].map((logo, index) => (
              <div key={`logo-1-${index}`} className="shrink-0">
                <img
                  src={logo}
                  alt={`Burok Logo ${index + 1}`}
                  className="h-6 w-auto object-contain opacity-90 transition-opacity duration-300 hover:opacity-100 sm:h-8 md:h-10"
                />
              </div>
            ))}
            {[
              '/assets/BurokLogoModern/BurokModernLogoWhite.png',
              '/assets/BurokLogoModern/BurokModernLogoBlack.png',
              '/assets/BurokLogoModern/BurokModernLogoOrange.png',
              '/assets/BurokLogoModern/BurokModernLogoPurple.png',
              '/assets/BurokLogoModern/BurokModernLogoBlue.png',
              '/assets/BurokLogoModern/BurokModernLogoYellow.png',
              '/assets/BurokLogoModern/BurokModernLogoGreen.png',
              '/assets/BurokLogoModern/BurokModernLogoRed.png',
            ].map((logo, index) => (
              <div key={`logo-2-${index}`} className="shrink-0">
                <img
                  src={logo}
                  alt={`Burok Logo ${index + 1}`}
                  className="h-6 w-auto object-contain opacity-90 transition-opacity duration-300 hover:opacity-100 sm:h-8 md:h-10"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="mx-auto max-w-4xl px-4 pb-12 pt-8">
          {/* Profile Header */}
          <div className="mb-8 text-center">
            <div className="relative -mt-20 mb-4 inline-block">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 blur-xl opacity-50" />
              <img
                src={businessProfile.logo_url || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=200&auto=format&fit=crop'}
                alt={businessProfile.business_name}
                className="relative h-32 w-32 rounded-full border-4 border-white object-cover shadow-2xl dark:border-neutral-800 sm:h-40 sm:w-40"
              />
            </div>
            <div className="mb-2 flex items-center justify-center gap-2">
              <h1 className="text-3xl font-bold text-neutral-900 dark:text-white sm:text-4xl">
                {businessProfile.business_name}
              </h1>
              {(businessProfile.is_verified || businessProfile.badge_verified) && (
                <img
                  src="/assets/images/verified-check-svgrepo-com.svg"
                  alt="Vérifié"
                  className="mt-1 h-6 w-6 sm:mt-1.5 sm:h-7 sm:w-7"
                />
              )}
            </div>

            {businessProfile.business_description && (
              <p className="mx-auto max-w-2xl text-lg text-neutral-600 dark:text-neutral-300 mb-6">
                {businessProfile.business_description}
              </p>
            )}

            {/* Social Links - Horizontal */}
            {businessSocialLinks.length > 0 && (
              <div className="mb-6 flex flex-wrap items-center justify-center gap-3">
                {businessSocialLinks.map((link) => (
                  <a
                    key={link.platform}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-2 rounded-xl border border-neutral-200 bg-white/80 px-4 py-2.5 text-sm transition-all duration-200 hover:scale-105 hover:border-blue-500 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:shadow-md dark:border-neutral-700 dark:bg-neutral-800/80 dark:hover:border-blue-400 dark:hover:from-blue-900/20 dark:hover:to-purple-900/20"
                    title={link.platform}
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-sm transition-transform duration-200 group-hover:scale-110">
                      {(() => {
                        const icons: Record<string, React.FC> = {
                          instagram: InstagramIcon,
                          twitter: TwitterIcon,
                          tiktok: TikTokIcon,
                          snapchat: SnapchatIcon,
                          linkedin: LinkedInIcon,
                          youtube: YoutubeIcon,
                          facebook: FacebookIcon
                        };
                        const IconComponent = icons[link.platform.toLowerCase()];
                        return IconComponent ? <IconComponent /> : null;
                      })()}
                    </div>
                    <span className="hidden font-medium text-neutral-700 dark:text-neutral-200 sm:inline">
                      @{link.username}
                    </span>
                  </a>
                ))}
              </div>
            )}

            {/* Contact Info - Affiché seulement si show_contact_info_public est true */}
            {showContactInfoPublic && (
              <div className="mb-6 flex flex-wrap items-center justify-center gap-4">
                {businessProfile.street && businessProfile.city && (
                  <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                    <MapPinIcon className="h-4 w-4" />
                    <span>{businessProfile.street}, {businessProfile.city}</span>
                  </div>
                )}
                {businessProfile.business_phone && (
                  <a
                    href={`tel:${businessProfile.business_phone}`}
                    className="flex items-center gap-2 text-sm text-neutral-600 transition-colors hover:text-blue-600 dark:text-neutral-400 dark:hover:text-blue-400"
                  >
                    <PhoneIcon className="h-4 w-4" />
                    <span>{businessProfile.business_phone}</span>
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Marketplaces Navigation */}
          <div className="mb-8">
            {/* Navbar pour naviguer entre les marketplaces */}
            <div className="mb-6 flex items-center justify-center gap-2 overflow-x-auto rounded-2xl border border-neutral-200 bg-white/80 p-2 backdrop-blur-sm dark:border-neutral-700 dark:bg-neutral-800/80 sm:gap-3">
              {(['kiosk', 'boutique', 'restaurant'] as const).map((shopType) => {
                const isActive = businessActiveTab === shopType;
                const count = shopCounts[shopType];
                const logoUrl = logos[shopType];

                return (
                  <button
                    key={shopType}
                    onClick={() => setBusinessActiveTab(shopType)}
                    className={`flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-xl px-2 py-2.5 transition-all duration-200 sm:gap-3 sm:px-4 sm:py-3 md:px-6 ${
                      isActive
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg shadow-blue-500/30'
                        : count > 0
                        ? 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-600'
                        : 'bg-neutral-50 text-neutral-400 opacity-60 dark:bg-neutral-800/50 dark:text-neutral-500'
                    }`}
                  >
                    {logoUrl ? (
                      <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg p-1 sm:h-8 sm:w-8 sm:p-1.5 md:h-10 md:w-10 ${
                        isActive ? 'bg-white/20' : 'bg-white shadow-sm ring-1 ring-neutral-200 dark:bg-neutral-800 dark:ring-neutral-600'
                      }`}>
                        <img
                          src={logoUrl}
                          alt={shopNames[shopType]}
                          className="h-full w-full object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      </div>
                    ) : (
                      <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-base sm:h-8 sm:w-8 sm:text-lg md:h-10 md:w-10 md:text-xl ${
                        isActive ? 'bg-white/20' : 'bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-sm'
                      }`}>
                        {shopType === 'kiosk' ? '🛒' : shopType === 'boutique' ? '👗' : '🍽️'}
                      </div>
                    )}
                    <span className={`whitespace-nowrap text-xs font-semibold sm:text-sm md:text-base ${
                      isActive ? 'text-white' : ''
                    }`}>
                      {shopNames[shopType]}
                    </span>
                    {count > 0 && (
                      <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium sm:px-2 sm:py-0.5 sm:text-xs md:px-2.5 md:text-sm ${
                        isActive
                          ? 'bg-white/30 text-white'
                          : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                      }`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Contenu de la marketplace active */}
            <div>
              {businessActiveTab === 'kiosk' && (
                <div>
                  {businessKiosks.length > 0 ? (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {businessKiosks.map((kiosk) => {
                        const isFollowing = followingStates[kiosk.id] || false;
                        const isAnimating = followingAnimating[kiosk.id] || false;
                        return (
                          <div
                            key={kiosk.id}
                            className="group relative overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm transition-all duration-300 hover:shadow-xl dark:border-neutral-700 dark:bg-neutral-800"
                          >
                            <Link to={`/kiosk/${kiosk.id}`} className="block">
                              <div className="relative h-48 w-full overflow-hidden">
                                <img
                                  src={kiosk.banner_url || kiosk.logo_url || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&auto=format&fit=crop'}
                                  alt={kiosk.business_name}
                                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                                <div className="absolute bottom-4 left-4 right-4">
                                  <div className="flex items-center gap-2">
                                    <img
                                      src={kiosk.logo_url || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=100&auto=format&fit=crop'}
                                      alt={kiosk.business_name}
                                      className="h-12 w-12 rounded-full border-2 border-white object-cover shadow-lg"
                                    />
                                    <div className="flex-1">
                                      <h3 className="font-bold text-white text-lg line-clamp-1">
                                        {kiosk.business_name}
                                      </h3>
                                      <div className="flex items-center gap-2 text-white/90 text-sm">
                                        <StarIcon className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                        <span>{(kiosk.rating || 4.8).toFixed(1)}</span>
                                        {kiosk.is_open && (
                                          <span className="ml-2 flex items-center gap-1">
                                            <div className="h-2 w-2 rounded-full bg-green-400" />
                                            Ouvert
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Link>
                            <div className="p-4">
                              {kiosk.business_description && (
                                <p className="mb-4 line-clamp-2 text-sm text-neutral-600 dark:text-neutral-400">
                                  {kiosk.business_description}
                                </p>
                              )}
                              <div className="flex gap-2">
                                <Link
                                  to={`/kiosk/${kiosk.id}`}
                                  className="flex-1 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-2 text-center text-sm font-medium text-white transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/30 hover:scale-105"
                                >
                                  Visiter
                                </Link>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleFollow(kiosk.id);
                                  }}
                                  className={`relative flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300 ${
                                    isFollowing
                                      ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-500/30'
                                      : 'border-2 border-neutral-300 bg-white text-neutral-700 hover:border-pink-400 hover:text-pink-600 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:border-pink-500 dark:hover:text-pink-400'
                                  } ${isAnimating ? 'scale-95' : 'scale-100 hover:scale-105'}`}
                                >
                                  {isFollowing ? (
                                    <>
                                      <HeartIconSolid className="h-5 w-5 animate-pulse" />
                                      <span>Suivi</span>
                                    </>
                                  ) : (
                                    <>
                                      <HeartIcon className="h-5 w-5" />
                                      <span>Suivre</span>
                                    </>
                                  )}
                                  {isAnimating && (
                                    <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-pink-400 to-rose-400 animate-ping opacity-75" />
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-neutral-200 bg-white/50 p-8 text-center dark:border-neutral-700 dark:bg-neutral-800/50">
                      <p className="text-lg text-neutral-600 dark:text-neutral-300">
                        Aucun kiosk disponible pour le moment
                      </p>
                    </div>
                  )}
                </div>
              )}

              {businessActiveTab === 'boutique' && (
                <div>
                  {businessBoutiques.length > 0 ? (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {businessBoutiques.map((boutique) => {
                        const isFollowing = followingStates[boutique.id] || false;
                        const isAnimating = followingAnimating[boutique.id] || false;
                        return (
                          <div
                            key={boutique.id}
                            className="group relative overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm transition-all duration-300 hover:shadow-xl dark:border-neutral-700 dark:bg-neutral-800"
                          >
                            <Link to={`/boutique/${boutique.id}`} className="block">
                              <div className="relative h-48 w-full overflow-hidden">
                                <img
                                  src={boutique.banner_url || boutique.logo_url || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&auto=format&fit=crop'}
                                  alt={boutique.business_name}
                                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                                <div className="absolute bottom-4 left-4 right-4">
                                  <div className="flex items-center gap-2">
                                    <img
                                      src={boutique.logo_url || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=100&auto=format&fit=crop'}
                                      alt={boutique.business_name}
                                      className="h-12 w-12 rounded-full border-2 border-white object-cover shadow-lg"
                                    />
                                    <div className="flex-1">
                                      <h3 className="font-bold text-white text-lg line-clamp-1">
                                        {boutique.business_name}
                                      </h3>
                                      <div className="flex items-center gap-2 text-white/90 text-sm">
                                        <StarIcon className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                        <span>{(boutique.rating || 4.8).toFixed(1)}</span>
                                        {boutique.is_open && (
                                          <span className="ml-2 flex items-center gap-1">
                                            <div className="h-2 w-2 rounded-full bg-green-400" />
                                            Ouvert
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Link>
                            <div className="p-4">
                              {boutique.business_description && (
                                <p className="mb-4 line-clamp-2 text-sm text-neutral-600 dark:text-neutral-400">
                                  {boutique.business_description}
                                </p>
                              )}
                              <div className="flex gap-2">
                                <Link
                                  to={`/boutique/${boutique.id}`}
                                  className="flex-1 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-2 text-center text-sm font-medium text-white transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/30 hover:scale-105"
                                >
                                  Visiter
                                </Link>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleFollow(boutique.id);
                                  }}
                                  className={`relative flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300 ${
                                    isFollowing
                                      ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-500/30'
                                      : 'border-2 border-neutral-300 bg-white text-neutral-700 hover:border-pink-400 hover:text-pink-600 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:border-pink-500 dark:hover:text-pink-400'
                                  } ${isAnimating ? 'scale-95' : 'scale-100 hover:scale-105'}`}
                                >
                                  {isFollowing ? (
                                    <>
                                      <HeartIconSolid className="h-5 w-5 animate-pulse" />
                                      <span>Suivi</span>
                                    </>
                                  ) : (
                                    <>
                                      <HeartIcon className="h-5 w-5" />
                                      <span>Suivre</span>
                                    </>
                                  )}
                                  {isAnimating && (
                                    <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-pink-400 to-rose-400 animate-ping opacity-75" />
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-neutral-200 bg-white/50 p-8 text-center dark:border-neutral-700 dark:bg-neutral-800/50">
                      <p className="text-lg text-neutral-600 dark:text-neutral-300">
                        Aucune boutique disponible pour le moment
                      </p>
                    </div>
                  )}
                </div>
              )}

              {businessActiveTab === 'restaurant' && (
                <div>
                  {businessRestaurants.length > 0 ? (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {businessRestaurants.map((restaurant) => {
                        const isFollowing = followingStates[restaurant.id] || false;
                        const isAnimating = followingAnimating[restaurant.id] || false;
                        return (
                          <div
                            key={restaurant.id}
                            className="group relative overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm transition-all duration-300 hover:shadow-xl dark:border-neutral-700 dark:bg-neutral-800"
                          >
                            <Link to={`/restaurant/${restaurant.id}`} className="block">
                              <div className="relative h-48 w-full overflow-hidden">
                                <img
                                  src={restaurant.banner_url || restaurant.logo_url || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&auto=format&fit=crop'}
                                  alt={restaurant.business_name}
                                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                                <div className="absolute bottom-4 left-4 right-4">
                                  <div className="flex items-center gap-2">
                                    <img
                                      src={restaurant.logo_url || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=100&auto=format&fit=crop'}
                                      alt={restaurant.business_name}
                                      className="h-12 w-12 rounded-full border-2 border-white object-cover shadow-lg"
                                    />
                                    <div className="flex-1">
                                      <h3 className="font-bold text-white text-lg line-clamp-1">
                                        {restaurant.business_name}
                                      </h3>
                                      <div className="flex items-center gap-2 text-white/90 text-sm">
                                        <StarIcon className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                        <span>{(restaurant.rating || 4.8).toFixed(1)}</span>
                                        {restaurant.is_open && (
                                          <span className="ml-2 flex items-center gap-1">
                                            <div className="h-2 w-2 rounded-full bg-green-400" />
                                            Ouvert
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Link>
                            <div className="p-4">
                              {restaurant.business_description && (
                                <p className="mb-4 line-clamp-2 text-sm text-neutral-600 dark:text-neutral-400">
                                  {restaurant.business_description}
                                </p>
                              )}
                              <div className="flex gap-2">
                                <Link
                                  to={`/restaurant/${restaurant.id}`}
                                  className="flex-1 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-2 text-center text-sm font-medium text-white transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/30 hover:scale-105"
                                >
                                  Visiter
                                </Link>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleFollow(restaurant.id);
                                  }}
                                  className={`relative flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300 ${
                                    isFollowing
                                      ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-500/30'
                                      : 'border-2 border-neutral-300 bg-white text-neutral-700 hover:border-pink-400 hover:text-pink-600 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:border-pink-500 dark:hover:text-pink-400'
                                  } ${isAnimating ? 'scale-95' : 'scale-100 hover:scale-105'}`}
                                >
                                  {isFollowing ? (
                                    <>
                                      <HeartIconSolid className="h-5 w-5 animate-pulse" />
                                      <span>Suivi</span>
                                    </>
                                  ) : (
                                    <>
                                      <HeartIcon className="h-5 w-5" />
                                      <span>Suivre</span>
                                    </>
                                  )}
                                  {isAnimating && (
                                    <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-pink-400 to-rose-400 animate-ping opacity-75" />
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-neutral-200 bg-white/50 p-8 text-center dark:border-neutral-700 dark:bg-neutral-800/50">
                      <p className="text-lg text-neutral-600 dark:text-neutral-300">
                        Aucun restaurant disponible pour le moment
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ') || profile.username || 'Utilisateur';

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900">
      {/* Banner */}
      {profile.banner_image_url && (
        <div className="relative h-48 w-full overflow-hidden sm:h-64 md:h-80">
          <img
            src={profile.banner_image_url}
            alt="Banner"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          {/* Logo ShopLink au centre */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-white/20 backdrop-blur-sm blur-xl" />
              <img
                src="/assets/images/Shoplinkpng.png"
                alt="ShopLink Logo"
                className="relative h-16 w-auto object-contain drop-shadow-2xl sm:h-20 md:h-24"
              />
            </div>
          </div>
        </div>
      )}

      {/* Bannière défilante avec logos Burok */}
      <div className="relative w-full overflow-hidden bg-gradient-to-r from-[#ff0442] via-[#ff1a4d] to-[#ff0442] py-3">
        <div className="flex animate-scroll-left items-center gap-8 whitespace-nowrap">
          {/* Dupliquer les logos pour un défilement infini */}
          {[
            '/assets/BurokLogoModern/BurokModernLogoWhite.png',
            '/assets/BurokLogoModern/BurokModernLogoBlack.png',
            '/assets/BurokLogoModern/BurokModernLogoOrange.png',
            '/assets/BurokLogoModern/BurokModernLogoPurple.png',
            '/assets/BurokLogoModern/BurokModernLogoBlue.png',
            '/assets/BurokLogoModern/BurokModernLogoYellow.png',
            '/assets/BurokLogoModern/BurokModernLogoGreen.png',
            '/assets/BurokLogoModern/BurokModernLogoRed.png',
            '/assets/BurokLogoModern/BurokModernLogoWhite.png',
            '/assets/BurokLogoModern/BurokModernLogoBlack.png',
            '/assets/BurokLogoModern/BurokModernLogoOrange.png',
            '/assets/BurokLogoModern/BurokModernLogoPurple.png',
            '/assets/BurokLogoModern/BurokModernLogoBlue.png',
            '/assets/BurokLogoModern/BurokModernLogoYellow.png',
            '/assets/BurokLogoModern/BurokModernLogoGreen.png',
            '/assets/BurokLogoModern/BurokModernLogoRed.png',
          ].map((logo, index) => (
            <div key={`logo-1-${index}`} className="flex-shrink-0">
              <img
                src={logo}
                alt={`Burok Logo ${index + 1}`}
                className="h-6 w-auto object-contain opacity-90 transition-opacity duration-300 hover:opacity-100 sm:h-8 md:h-10"
              />
            </div>
          ))}
          {/* Dupliquer pour un défilement continu */}
          {[
            '/assets/BurokLogoModern/BurokModernLogoWhite.png',
            '/assets/BurokLogoModern/BurokModernLogoBlack.png',
            '/assets/BurokLogoModern/BurokModernLogoOrange.png',
            '/assets/BurokLogoModern/BurokModernLogoPurple.png',
            '/assets/BurokLogoModern/BurokModernLogoBlue.png',
            '/assets/BurokLogoModern/BurokModernLogoYellow.png',
            '/assets/BurokLogoModern/BurokModernLogoGreen.png',
            '/assets/BurokLogoModern/BurokModernLogoRed.png',
          ].map((logo, index) => (
            <div key={`logo-2-${index}`} className="flex-shrink-0">
              <img
                src={logo}
                alt={`Burok Logo ${index + 1}`}
                className="h-6 w-auto object-contain opacity-90 transition-opacity duration-300 hover:opacity-100 sm:h-8 md:h-10"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 pb-12 pt-8">
        {/* Profile Header */}
        <div className="mb-8 text-center">
          <div className="relative -mt-20 mb-4 inline-block">
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 blur-xl opacity-50" />
            <img
              src={profile.avatar_url || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&auto=format&fit=crop'}
              alt={fullName}
              className="relative h-32 w-32 rounded-full border-4 border-white object-cover shadow-2xl dark:border-neutral-800 sm:h-40 sm:w-40"
            />
          </div>
          <div className="mb-2 flex items-center justify-center gap-2">
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white sm:text-4xl">
            {fullName}
          </h1>
            {isVerified && (
              <img
                src="/assets/images/verified-check-svgrepo-com.svg"
                alt="Vérifié"
                className="mt-1 h-6 w-6 sm:mt-1.5 sm:h-7 sm:w-7"
              />
            )}
          </div>
          {profile.bio && (
            <p className="mx-auto max-w-2xl text-lg text-neutral-600 dark:text-neutral-300 mb-6">
              {profile.bio}
            </p>
          )}
          
          {/* Social Links - Horizontal */}
          {socialLinks.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-3">
              {socialLinks.map((link) => (
                <a
                  key={link.platform}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 rounded-xl border border-neutral-200 bg-white/80 px-4 py-2.5 text-sm transition-all duration-200 hover:scale-105 hover:border-blue-500 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:shadow-md dark:border-neutral-700 dark:bg-neutral-800/80 dark:hover:border-blue-400 dark:hover:from-blue-900/20 dark:hover:to-purple-900/20"
                  title={link.platform}
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-sm transition-transform duration-200 group-hover:scale-110">
                    {(() => {
                      const icons: Record<string, React.FC> = {
                        instagram: InstagramIcon,
                        twitter: TwitterIcon,
                        tiktok: TikTokIcon,
                        snapchat: SnapchatIcon,
                        linkedin: LinkedInIcon,
                        youtube: YoutubeIcon,
                        facebook: FacebookIcon
                      };
                      const IconComponent = icons[link.platform.toLowerCase()];
                      return IconComponent ? <IconComponent /> : null;
                    })()}
                  </div>
                  <span className="hidden font-medium text-neutral-700 dark:text-neutral-200 sm:inline">
                    @{link.username}
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Marketplaces Navigation */}
        {products && (
          <div className="mb-8">
            {/* Navbar pour naviguer entre les marketplaces */}
            <div className="mb-6 flex items-center justify-center gap-2 overflow-x-auto rounded-2xl border border-neutral-200 bg-white/80 p-2 backdrop-blur-sm dark:border-neutral-700 dark:bg-neutral-800/80 sm:gap-3">
              {(['kiosk', 'boutique', 'restaurant'] as const).map((shopType) => {
                const shopNames: Record<'kiosk' | 'boutique' | 'restaurant', string> = {
                  kiosk: 'Kiosk',
                  boutique: 'Boutiques',
                  restaurant: 'Restaurants'
                };
                const shopLogos: Record<'kiosk' | 'boutique' | 'restaurant', string | undefined> = {
                  kiosk: logos.kiosk,
                  boutique: logos.boutique,
                  restaurant: logos.restaurant
                };
                const storeCounts: Record<'kiosk' | 'boutique' | 'restaurant', number> = {
                  kiosk: userKiosks.length,
                  boutique: userBoutiques.length,
                  restaurant: userRestaurants.length
                };
                const isActive = activeTab === shopType;
                const isShopActivated = activeShops.includes(shopType);
                const logoUrl = shopLogos[shopType];
                const storeCount = storeCounts[shopType];

                return (
                  <button
                    key={shopType}
                    onClick={() => setActiveTab(shopType)}
                    className={`flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-xl px-2 py-2.5 transition-all duration-200 sm:gap-3 sm:px-4 sm:py-3 md:px-6 ${
                      isActive
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg shadow-blue-500/30'
                        : isShopActivated
                        ? 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-600'
                        : 'bg-neutral-50 text-neutral-400 opacity-60 dark:bg-neutral-800/50 dark:text-neutral-500'
                    }`}
                  >
                    {logoUrl ? (
                      <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg p-1 sm:h-8 sm:w-8 sm:p-1.5 md:h-10 md:w-10 ${
                        isActive ? 'bg-white/20' : 'bg-white shadow-sm ring-1 ring-neutral-200 dark:bg-neutral-800 dark:ring-neutral-600'
                      }`}>
                        <img
                          src={logoUrl}
                          alt={shopNames[shopType]}
                          className="h-full w-full object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      </div>
                    ) : (
                      <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-base sm:h-8 sm:w-8 sm:text-lg md:h-10 md:w-10 md:text-xl ${
                        isActive ? 'bg-white/20' : 'bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-sm'
                      }`}>
                        {shopType === 'kiosk' ? '🛒' : shopType === 'boutique' ? '👗' : '🍽️'}
                      </div>
                    )}
                    <span className={`whitespace-nowrap text-xs font-semibold sm:text-sm md:text-base ${
                      isActive ? 'text-white' : ''
                    }`}>
                      {shopNames[shopType]}
                    </span>
                    {storeCount > 0 && (
                      <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium sm:px-2 sm:py-0.5 sm:text-xs md:px-2.5 md:text-sm ${
                        isActive
                          ? 'bg-white/30 text-white'
                          : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                      }`}>
                        {storeCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Contenu de la marketplace active */}
            {activeTab && (
              <div>
                {activeTab === 'kiosk' && (
                  <div>
                    {userKiosks.length > 0 ? (
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {userKiosks.map((kiosk) => {
                          const isFollowing = userFollowingStates[kiosk.id] || false;
                          const isAnimating = userFollowingAnimating[kiosk.id] || false;
                          return (
                            <div
                              key={kiosk.id}
                              className="group relative overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm transition-all duration-300 hover:shadow-xl dark:border-neutral-700 dark:bg-neutral-800"
                            >
                              <Link to={`/kiosk/${kiosk.id}`} className="block">
                                <div className="relative h-48 w-full overflow-hidden">
                                  <img
                                    src={kiosk.banner_url || kiosk.logo_url || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&auto=format&fit=crop'}
                                    alt={kiosk.business_name}
                                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                                  />
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                                  <div className="absolute bottom-4 left-4 right-4">
                                    <div className="flex items-center gap-2">
                                      <img
                                        src={kiosk.logo_url || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=100&auto=format&fit=crop'}
                                        alt={kiosk.business_name}
                                        className="h-12 w-12 rounded-full border-2 border-white object-cover shadow-lg"
                                      />
                                      <div className="flex-1">
                                        <h3 className="font-bold text-white text-lg line-clamp-1">
                                          {kiosk.business_name}
                                        </h3>
                                        <div className="flex items-center gap-2 text-white/90 text-sm">
                                          <StarIcon className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                          <span>{(kiosk.rating || 4.8).toFixed(1)}</span>
                                          {kiosk.is_open && (
                                            <span className="ml-2 flex items-center gap-1">
                                              <div className="h-2 w-2 rounded-full bg-green-400" />
                                              Ouvert
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </Link>
                              <div className="p-4">
                                {kiosk.business_description && (
                                  <p className="mb-4 line-clamp-2 text-sm text-neutral-600 dark:text-neutral-400">
                                    {kiosk.business_description}
                                  </p>
                                )}
                                <div className="flex gap-2">
                                  <Link
                                    to={`/kiosk/${kiosk.id}`}
                                    className="flex-1 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-2 text-center text-sm font-medium text-white transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/30 hover:scale-105"
                                  >
                                    Visiter
                                  </Link>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleUserFollow(kiosk.id);
                                    }}
                                    className={`relative flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300 ${
                                      isFollowing
                                        ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-500/30'
                                        : 'border-2 border-neutral-300 bg-white text-neutral-700 hover:border-pink-400 hover:text-pink-600 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:border-pink-500 dark:hover:text-pink-400'
                                    } ${isAnimating ? 'scale-95' : 'scale-100 hover:scale-105'}`}
                                  >
                                    {isFollowing ? (
                                      <>
                                        <HeartIconSolid className="h-5 w-5 animate-pulse" />
                                        <span>Suivi</span>
                                      </>
                                    ) : (
                                      <>
                                        <HeartIcon className="h-5 w-5" />
                                        <span>Suivre</span>
                                      </>
                                    )}
                                    {isAnimating && (
                                      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-pink-400 to-rose-400 animate-ping opacity-75" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-neutral-200 bg-white/50 p-8 text-center dark:border-neutral-700 dark:bg-neutral-800/50">
                        <p className="text-lg text-neutral-600 dark:text-neutral-300">
                          {profile?.first_name || 'Cet utilisateur'} n'a pas encore de Kiosk.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {activeTab === 'boutique' && (
                  <div>
                    {userBoutiques.length > 0 ? (
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {userBoutiques.map((boutique) => {
                          const isFollowing = userFollowingStates[boutique.id] || false;
                          const isAnimating = userFollowingAnimating[boutique.id] || false;
                          return (
                            <div
                              key={boutique.id}
                              className="group relative overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm transition-all duration-300 hover:shadow-xl dark:border-neutral-700 dark:bg-neutral-800"
                            >
                              <Link to={`/boutique/${boutique.id}`} className="block">
                                <div className="relative h-48 w-full overflow-hidden">
                                  <img
                                    src={boutique.banner_url || boutique.logo_url || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&auto=format&fit=crop'}
                                    alt={boutique.business_name}
                                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                                  />
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                                  <div className="absolute bottom-4 left-4 right-4">
                                    <div className="flex items-center gap-2">
                                      <img
                                        src={boutique.logo_url || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=100&auto=format&fit=crop'}
                                        alt={boutique.business_name}
                                        className="h-12 w-12 rounded-full border-2 border-white object-cover shadow-lg"
                                      />
                                      <div className="flex-1">
                                        <h3 className="font-bold text-white text-lg line-clamp-1">
                                          {boutique.business_name}
                                        </h3>
                                        <div className="flex items-center gap-2 text-white/90 text-sm">
                                          <StarIcon className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                          <span>{(boutique.rating || 4.8).toFixed(1)}</span>
                                          {boutique.is_open && (
                                            <span className="ml-2 flex items-center gap-1">
                                              <div className="h-2 w-2 rounded-full bg-green-400" />
                                              Ouvert
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </Link>
                              <div className="p-4">
                                {boutique.business_description && (
                                  <p className="mb-4 line-clamp-2 text-sm text-neutral-600 dark:text-neutral-400">
                                    {boutique.business_description}
                                  </p>
                                )}
                                <div className="flex gap-2">
                                  <Link
                                    to={`/boutique/${boutique.id}`}
                                    className="flex-1 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-2 text-center text-sm font-medium text-white transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/30 hover:scale-105"
                                  >
                                    Visiter
                                  </Link>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleUserFollow(boutique.id);
                                    }}
                                    className={`relative flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300 ${
                                      isFollowing
                                        ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-500/30'
                                        : 'border-2 border-neutral-300 bg-white text-neutral-700 hover:border-pink-400 hover:text-pink-600 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:border-pink-500 dark:hover:text-pink-400'
                                    } ${isAnimating ? 'scale-95' : 'scale-100 hover:scale-105'}`}
                                  >
                                    {isFollowing ? (
                                      <>
                                        <HeartIconSolid className="h-5 w-5 animate-pulse" />
                                        <span>Suivi</span>
                                      </>
                                    ) : (
                                      <>
                                        <HeartIcon className="h-5 w-5" />
                                        <span>Suivre</span>
                                      </>
                                    )}
                                    {isAnimating && (
                                      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-pink-400 to-rose-400 animate-ping opacity-75" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-neutral-200 bg-white/50 p-8 text-center dark:border-neutral-700 dark:bg-neutral-800/50">
                        <p className="text-lg text-neutral-600 dark:text-neutral-300">
                          {profile?.first_name || 'Cet utilisateur'} n'a pas encore de Boutique.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {activeTab === 'restaurant' && (
                  <div>
                    {userRestaurants.length > 0 ? (
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {userRestaurants.map((restaurant) => {
                          const isFollowing = userFollowingStates[restaurant.id] || false;
                          const isAnimating = userFollowingAnimating[restaurant.id] || false;
                          return (
                            <div
                              key={restaurant.id}
                              className="group relative overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm transition-all duration-300 hover:shadow-xl dark:border-neutral-700 dark:bg-neutral-800"
                            >
                              <Link to={`/restaurant/${restaurant.id}`} className="block">
                                <div className="relative h-48 w-full overflow-hidden">
                                  <img
                                    src={restaurant.banner_url || restaurant.logo_url || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&auto=format&fit=crop'}
                                    alt={restaurant.business_name}
                                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                                  />
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                                  <div className="absolute bottom-4 left-4 right-4">
                                    <div className="flex items-center gap-2">
                                      <img
                                        src={restaurant.logo_url || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=100&auto=format&fit=crop'}
                                        alt={restaurant.business_name}
                                        className="h-12 w-12 rounded-full border-2 border-white object-cover shadow-lg"
                                      />
                                      <div className="flex-1">
                                        <h3 className="font-bold text-white text-lg line-clamp-1">
                                          {restaurant.business_name}
                                        </h3>
                                        <div className="flex items-center gap-2 text-white/90 text-sm">
                                          <StarIcon className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                          <span>{(restaurant.rating || 4.8).toFixed(1)}</span>
                                          {restaurant.is_open && (
                                            <span className="ml-2 flex items-center gap-1">
                                              <div className="h-2 w-2 rounded-full bg-green-400" />
                                              Ouvert
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </Link>
                              <div className="p-4">
                                {restaurant.business_description && (
                                  <p className="mb-4 line-clamp-2 text-sm text-neutral-600 dark:text-neutral-400">
                                    {restaurant.business_description}
                                  </p>
                                )}
                                <div className="flex gap-2">
                                  <Link
                                    to={`/restaurant/${restaurant.id}`}
                                    className="flex-1 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-2 text-center text-sm font-medium text-white transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/30 hover:scale-105"
                                  >
                                    Visiter
                                  </Link>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleUserFollow(restaurant.id);
                                    }}
                                    className={`relative flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300 ${
                                      isFollowing
                                        ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-500/30'
                                        : 'border-2 border-neutral-300 bg-white text-neutral-700 hover:border-pink-400 hover:text-pink-600 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:border-pink-500 dark:hover:text-pink-400'
                                    } ${isAnimating ? 'scale-95' : 'scale-100 hover:scale-105'}`}
                                  >
                                    {isFollowing ? (
                                      <>
                                        <HeartIconSolid className="h-5 w-5 animate-pulse" />
                                        <span>Suivi</span>
                                      </>
                                    ) : (
                                      <>
                                        <HeartIcon className="h-5 w-5" />
                                        <span>Suivre</span>
                                      </>
                                    )}
                                    {isAnimating && (
                                      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-pink-400 to-rose-400 animate-ping opacity-75" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-neutral-200 bg-white/50 p-8 text-center dark:border-neutral-700 dark:bg-neutral-800/50">
                        <p className="text-lg text-neutral-600 dark:text-neutral-300">
                          {profile?.first_name || 'Cet utilisateur'} n'a pas encore de Restaurant.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

